import { useEffect, useRef } from 'react';
import type { TranslationKey } from '../i18n.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * L'aide — ce que l'application sait faire **aujourd'hui**.
 *
 * ⚠️ Elle décrit l'existant, jamais l'intention. Une aide qui annonce une fonction à venir
 * se lit comme une fonction absente : la personne la cherche, ne la trouve pas, et conclut
 * que l'outil est cassé. Ce qui n'existe pas encore n'a pas sa place ici — il a sa place
 * dans le backlog.
 *
 * ⚠️ Et elle ne cite aucun chiffre qui bouge. L'étendue réelle de l'historique est dans la
 * frise « Ce que la base contient », qui la lit dans la base ; l'écrire ici en dur la
 * figerait au jour où elle a été écrite, et ce serait faux le mois suivant.
 */
export function Help({ t, onClose }: { t: T; onClose: () => void }) {
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Le fond ne défile pas pendant que la fenêtre est ouverte : sinon, sur mobile, on
    // fait défiler la page derrière en croyant faire défiler l'aide.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-title"
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-t-2xl border border-card-border bg-surface shadow-lg sm:rounded-2xl"
      >
        <div className="flex items-start gap-3 border-b border-border px-5 py-4">
          <div className="mr-auto">
            <h2 id="help-title" className="text-lg font-semibold">
              {t('help.title')}
            </h2>
            <p className="text-sm text-muted">{t('help.subtitle')}</p>
          </div>
          <button
            ref={closeButton}
            type="button"
            onClick={onClose}
            aria-label={t('help.close')}
            title={t('help.close')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-muted transition-colors hover:bg-bg hover:text-fg"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              className="h-[18px] w-[18px]"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto px-5 py-4 text-sm leading-relaxed">
          <p>{t('help.intro')}</p>

          <Section title={t('help.data.title')}>
            <p>{t('help.data.body')}</p>
            <p className="text-muted">{t('help.data.night')}</p>
          </Section>

          <Section title={t('help.views.title')}>
            <ul className="list-disc space-y-1 pl-5">
              <li>{t('help.views.jobs')}</li>
              <li>{t('help.views.summary')}</li>
              <li>{t('help.views.decompte')}</li>
            </ul>
          </Section>

          <Section title={t('help.filter.title')}>
            <p>{t('help.filter.body')}</p>
            <p>{t('help.filter.dates')}</p>
            <p>{t('help.filter.click')}</p>
          </Section>

          <Section title={t('help.costCenter.title')}>
            <p>{t('help.costCenter.body')}</p>
          </Section>

          <Section title={t('help.decompte.title')}>
            <p>{t('help.decompte.body')}</p>
            <p className="text-muted">{t('decompte.note')}</p>
          </Section>

          <Section title={t('help.sort.title')}>
            <p>{t('help.sort.body')}</p>
          </Section>

          <Section title={t('help.export.title')}>
            <p>{t('help.export.body')}</p>
            <p className="text-muted">{t('help.export.limit')}</p>
            {/* La transparence d'abord : on dit aux gens que leurs exports sont tracés, au
                lieu de le leur laisser découvrir. */}
            <p className="text-muted">{t('help.export.audit')}</p>
          </Section>

          <Section title={t('help.scope.title')}>
            <p>{t('help.scope.body')}</p>
            <p>{t('help.scope.visitors')}</p>
            <p className="text-muted">{t('help.scope.access')}</p>
          </Section>

          <Section title={t('help.privacy.title')}>
            <p>{t('help.privacy.body')}</p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="font-semibold">{title}</h3>
      {children}
    </section>
  );
}
